// SPDX-License-Identifier: GPL-3.0-or-later
// The feedback settings: sound and music on or off, their volume, the level of the visual effects,
// flashing, vibration, and the stream beside the table.
//
// One versioned local storage key. Version 2 adds the music, vibration and stream fields. A version 1
// value keeps its four fields and gets the defaults for the new fields. A value that is missing, not
// JSON, from another app, from another version, or not valid in any field gives the defaults.
// Storage, `matchMedia` and the connection probe are injected, so the store runs in a test with no
// window. A storage that throws (a blocked or full storage) never breaks the game: the choice stays
// for the page.
//
// Defaults follow the system: when `prefers-reduced-motion: reduce` matches, the effects are
// "reduced", flashing is off, and the stream background is "off" (a video is moving). When the
// connection is limited (data saver, or a slow network), the stream background is "off" too. A saved
// choice wins over the system on later visits.

/** The level of the visual effects. */
export type VfxLevel = "full" | "reduced" | "off";

/** The stream that the side panel beside the table shows, or no stream. */
export type StreamBackground = "twitch" | "off";

/** The settings. */
export interface FeedbackSettings {
  readonly soundEnabled: boolean;
  /** 0..1. The volume of the effects. */
  readonly volume: number;
  readonly vfxLevel: VfxLevel;
  /** True: no all-lamp colour wash (WCAG 2.3.1). Play has no full-screen flash at any setting. */
  readonly reduceFlashing: boolean;
  /** True: the background music plays. */
  readonly musicEnabled: boolean;
  /** 0..1. The volume of the music. */
  readonly musicVolume: number;
  /** True: the device vibrates on a touch event, when it can. */
  readonly haptics: boolean;
  /** The stream that the side panel shows, or no stream. */
  readonly streamBackground: StreamBackground;
}

/** The field names of the settings. */
type SettingKey = keyof FeedbackSettings;

/** The local storage key. The number is the version of the stored shape. */
export const SETTINGS_KEY = "pinball.feedback.v1";

/** The version in the stored value. */
export const SETTINGS_VERSION = 2;

/** The media query for reduced motion. */
export const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

/** The default volume of the effects. */
export const DEFAULT_VOLUME = 0.6;

/** The default volume of the music. */
export const DEFAULT_MUSIC_VOLUME = 0.4;

const VFX_LEVELS: readonly VfxLevel[] = ["full", "reduced", "off"];
const STREAM_BACKGROUNDS: readonly StreamBackground[] = ["twitch", "off"];
const SLOW_CONNECTIONS: readonly string[] = ["slow-2g", "2g", "3g"];
const SETTING_KEYS: readonly SettingKey[] = [
  "soundEnabled",
  "volume",
  "vfxLevel",
  "reduceFlashing",
  "musicEnabled",
  "musicVolume",
  "haptics",
  "streamBackground",
];
/** The fields that version 1 stored. */
const VERSION_1_KEYS: readonly SettingKey[] = [
  "soundEnabled",
  "volume",
  "vfxLevel",
  "reduceFlashing",
];

/** The part of `Storage` that the store uses. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** The part of `matchMedia` that the store uses. */
export type MatchMediaLike = (query: string) => { readonly matches: boolean } | null | undefined;

/** The part of `navigator.connection` that the store uses. */
export interface ConnectionLike {
  readonly saveData?: boolean;
  readonly effectiveType?: string;
}

/** Reads the connection. It may throw, and then the connection counts as fast. */
export type ConnectionProbe = () => ConnectionLike | null | undefined;

/** What the store takes from the platform. All are optional. */
export interface SettingsDeps {
  readonly storage?: StorageLike | null;
  readonly matchMedia?: MatchMediaLike | null;
  readonly connection?: ConnectionProbe | null;
}

/** A change listener. It gets the new settings. */
export type SettingsListener = (settings: FeedbackSettings) => void;

/** The store. */
export interface SettingsStore {
  /** The settings now. The same object stays until a value changes. */
  get(): FeedbackSettings;
  /** Change some values. A value that is not valid is ignored. A change is saved and announced. */
  set(patch: Partial<FeedbackSettings>): FeedbackSettings;
  /** Back to the defaults (they follow the system again). */
  reset(): FeedbackSettings;
  /** Add a listener for changes. The result removes it. */
  subscribe(listener: SettingsListener): () => void;
}

/** A rule for one field. It gives the checked value, or `undefined` when the value is not valid. */
type Rule<K extends SettingKey> = (value: unknown) => FeedbackSettings[K] | undefined;

/** One rule for each field. */
type Rules = { readonly [K in SettingKey]: Rule<K> };

/** True when the system asks for reduced motion. A missing or failing `matchMedia` gives false. */
export function prefersReducedMotion(matchMedia: MatchMediaLike | null | undefined): boolean {
  try {
    return matchMedia?.(REDUCED_MOTION_QUERY)?.matches === true;
  } catch {
    return false;
  }
}

/** True when the connection is limited: a data saver, or a slow network. */
function isLimitedConnection(connection: ConnectionLike | null): boolean {
  if (connection === null) return false;
  const { saveData, effectiveType } = connection;
  return (
    saveData === true ||
    (typeof effectiveType === "string" && SLOW_CONNECTIONS.includes(effectiveType))
  );
}

/** Read the connection probe. A probe that throws or gives nothing gives `null`. */
function readConnection(probe: ConnectionProbe | null | undefined): ConnectionLike | null {
  try {
    return probe?.() ?? null;
  } catch {
    return null;
  }
}

/**
 * The default stream: "off" on a limited connection or when the system asks for reduced motion (a
 * video moves), else "twitch" (a side panel; the page shows it only on a wide screen with a mouse).
 */
export function defaultStreamBackground(
  probe?: ConnectionProbe | null,
  matchMedia?: MatchMediaLike | null,
): StreamBackground {
  if (prefersReducedMotion(matchMedia)) return "off";
  return isLimitedConnection(readConnection(probe)) ? "off" : "twitch";
}

/** The defaults for the fields that version 1 did not have. */
function addedDefaults(
  probe: ConnectionProbe | null | undefined,
  matchMedia: MatchMediaLike | null | undefined,
): Pick<FeedbackSettings, "musicEnabled" | "musicVolume" | "haptics" | "streamBackground"> {
  return {
    musicEnabled: true,
    musicVolume: DEFAULT_MUSIC_VOLUME,
    haptics: true,
    streamBackground: defaultStreamBackground(probe, matchMedia),
  };
}

/** The defaults for a system. */
export function defaultSettings(
  matchMedia?: MatchMediaLike | null,
  connection?: ConnectionProbe | null,
): FeedbackSettings {
  const reduced = prefersReducedMotion(matchMedia);
  return {
    soundEnabled: true,
    volume: DEFAULT_VOLUME,
    vfxLevel: reduced ? "reduced" : "full",
    reduceFlashing: reduced,
    ...addedDefaults(connection, matchMedia),
  };
}

function isOneOf<T extends string>(list: readonly T[], value: unknown): value is T {
  return typeof value === "string" && (list as readonly string[]).includes(value);
}

function asBoolean(value: unknown): boolean | undefined {
  return typeof value === "boolean" ? value : undefined;
}

function asVfxLevel(value: unknown): VfxLevel | undefined {
  return isOneOf(VFX_LEVELS, value) ? value : undefined;
}

function asStreamBackground(value: unknown): StreamBackground | undefined {
  return isOneOf(STREAM_BACKGROUNDS, value) ? value : undefined;
}

/**
 * A stored stream choice. A text that is no choice now (an old "youtube" or an unknown name) loads
 * as "twitch". A value that is not a text is not valid.
 */
function asStoredStreamBackground(value: unknown): StreamBackground | undefined {
  if (typeof value !== "string") return undefined;
  return asStreamBackground(value) ?? "twitch";
}

/** A stored volume must already be in 0..1. */
function asVolume(value: unknown): number | undefined {
  const ok = typeof value === "number" && Number.isFinite(value) && value >= 0 && value <= 1;
  return ok ? value : undefined;
}

/** A finite number outside 0..1 goes to the nearest end. Anything else is not valid. */
function asClampedVolume(value: unknown): number | undefined {
  if (typeof value !== "number" || !Number.isFinite(value)) return undefined;
  return Math.min(1, Math.max(0, value));
}

/** The rules for a patch. A volume is clamped. */
const PATCH_RULES: Rules = {
  soundEnabled: asBoolean,
  volume: asClampedVolume,
  vfxLevel: asVfxLevel,
  reduceFlashing: asBoolean,
  musicEnabled: asBoolean,
  musicVolume: asClampedVolume,
  haptics: asBoolean,
  streamBackground: asStreamBackground,
};

/** The rules for a stored value. A volume out of range makes the whole value invalid. */
const STORED_RULES: Rules = {
  ...PATCH_RULES,
  volume: asVolume,
  musicVolume: asVolume,
  streamBackground: asStoredStreamBackground,
};

/**
 * Read the keys of a stored object. Return the values, or `null` when one of them is missing or not
 * valid.
 */
function readKeys(
  o: Record<string, unknown>,
  keys: readonly SettingKey[],
): Partial<FeedbackSettings> | null {
  const out: Partial<FeedbackSettings> = {};
  for (const key of keys) {
    const value = STORED_RULES[key](o[key]);
    if (value === undefined) return null;
    Object.assign(out, { [key]: value });
  }
  return out;
}

/** Version 2: every field must be valid. */
function readVersion2(o: Record<string, unknown>): FeedbackSettings | null {
  const fields = readKeys(o, SETTING_KEYS);
  return fields === null ? null : (fields as FeedbackSettings);
}

/** Version 1: the four old fields must be valid. The new fields get the defaults. */
function readVersion1(
  o: Record<string, unknown>,
  probe: ConnectionProbe | null | undefined,
  matchMedia: MatchMediaLike | null | undefined,
): FeedbackSettings | null {
  const kept = readKeys(o, VERSION_1_KEYS);
  return kept === null
    ? null
    : ({ ...addedDefaults(probe, matchMedia), ...kept } as FeedbackSettings);
}

/** Parse JSON. Text that is not JSON gives `undefined`. */
function parseJson(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
}

/**
 * Read a stored value. Return the settings, or `null` when the value is not valid. A version 2 value
 * has every field. A version 1 value is migrated: its fields are kept and the new fields get the
 * defaults. The connection probe and `matchMedia` give the default stream.
 */
export function parseSettings(
  raw: string | null | undefined,
  connection?: ConnectionProbe | null,
  matchMedia?: MatchMediaLike | null,
): FeedbackSettings | null {
  if (typeof raw !== "string" || raw === "") return null;
  const data = parseJson(raw);
  if (typeof data !== "object" || data === null || Array.isArray(data)) return null;
  const o = data as Record<string, unknown>;
  if (o.v === SETTINGS_VERSION) return readVersion2(o);
  if (o.v === 1) return readVersion1(o, connection, matchMedia);
  return null;
}

/** The text to store. */
export function serializeSettings(settings: FeedbackSettings): string {
  return JSON.stringify({ v: SETTINGS_VERSION, ...settings });
}

/** Merge a patch into the settings. A field that is not valid keeps the old value. */
export function applyPatch(
  base: FeedbackSettings,
  patch: Partial<FeedbackSettings>,
): FeedbackSettings {
  const next: FeedbackSettings = { ...base };
  for (const key of SETTING_KEYS) {
    const value = PATCH_RULES[key](patch[key]);
    if (value !== undefined) Object.assign(next, { [key]: value });
  }
  return next;
}

function same(a: FeedbackSettings, b: FeedbackSettings): boolean {
  return SETTING_KEYS.every((key) => a[key] === b[key]);
}

function readStored(
  storage: StorageLike | null | undefined,
  connection: ConnectionProbe | null | undefined,
  matchMedia: MatchMediaLike | null | undefined,
): FeedbackSettings | null {
  try {
    return parseSettings(storage?.getItem(SETTINGS_KEY), connection, matchMedia);
  } catch {
    return null;
  }
}

function writeStored(storage: StorageLike | null | undefined, settings: FeedbackSettings): void {
  try {
    storage?.setItem(SETTINGS_KEY, serializeSettings(settings));
  } catch {
    // The storage is blocked or full. The choice stays for this page.
  }
}

/** Make the store. It loads the saved value now. */
export function createSettingsStore(deps: SettingsDeps = {}): SettingsStore {
  const { storage, matchMedia, connection } = deps;
  let current: FeedbackSettings =
    readStored(storage, connection, matchMedia) ?? defaultSettings(matchMedia, connection);
  let listeners: readonly SettingsListener[] = [];

  const change = (next: FeedbackSettings): FeedbackSettings => {
    if (same(current, next)) return current;
    current = next;
    writeStored(storage, current);
    for (const listener of listeners) {
      try {
        listener(current);
      } catch {
        // A listener must not break the others.
      }
    }
    return current;
  };

  return {
    get: () => current,
    set: (patch) => change(applyPatch(current, patch)),
    reset: () => change(defaultSettings(matchMedia, connection)),
    subscribe(listener) {
      listeners = [...listeners, listener];
      return () => {
        listeners = listeners.filter((l) => l !== listener);
      };
    },
  };
}
